import * as ko from "knockout";
import { lm } from "../../appContext";
import toggle_eyeHtml from './toggle-eye.html?raw';

export let template: string = toggle_eyeHtml;

interface IParams {
    /** External toggle; created internally when omitted (e.g. syncfield secret reveal). */
    isToggled?: KnockoutObservable<boolean>;
    /** When set, reveal auto-hides after this many ms and shows a countdown fill. */
    duration?: number;
    /** Optional secret/value shown beside the eye while revealed. */
    text?: string;
    titleShow?: string;
    titleHide?: string;
}

export class viewModel {

    isToggled: KnockoutObservable<boolean>;
    duration: number | undefined;
    text: string | undefined;
    private titleShow = lm.t("Show") || "Show";
    private titleHide = lm.t("Hide") || "Hide";
    private timeoutId: number | undefined;
    private remainingMs: number | undefined;
    private deadlineAt: number | undefined;
    private hoverPaused = false;
    private focusPaused = false;
    private toggleSub: KnockoutSubscription | undefined;

    /** True while hover/focus holds the timed reveal open. */
    isPaused = ko.observable(false);

    constructor(params: IParams) {
        this.isToggled = params.isToggled ?? ko.observable(false);
        this.duration = params.duration;
        this.text = params.text;
        this.titleShow = params.titleShow || this.titleShow;
        this.titleHide = params.titleHide || this.titleHide;

        if (this.duration) {
            // Auto-hide when the timed reveal window elapses; clear on early hide.
            this.toggleSub = this.isToggled.subscribe((isRevealed) => {
                clearTimeout(this.timeoutId);
                this.timeoutId = undefined;
                this.deadlineAt = undefined;
                this.remainingMs = undefined;
                this.isPaused(false);

                if (isRevealed) {
                    this.remainingMs = this.duration;
                    if (this.hoverPaused || this.focusPaused) {
                        this.isPaused(true);
                    } else {
                        this.startTimer();
                    }
                }
            });
        }
    }

    tooltip = ko.pureComputed((): string => {
        return this.isToggled() ? this.titleHide : this.titleShow;
    });

    // Timed reveal: boxy chrome + fill animation while content is shown.
    isTiming = ko.pureComputed((): boolean => {
        return !!this.duration && this.isToggled();
    });

    durationStyle = ko.pureComputed((): Record<string, string> | undefined => {
        if (!this.isTiming()) {
            return undefined;
        }
        return {'--duration': `${this.duration}ms`};
    });

    onClick() {
        this.isToggled(!this.isToggled());
    }

    onMouseEnter() {
        this.setHoverPaused(true);
    }

    onMouseLeave() {
        this.setHoverPaused(false);
    }

    onFocus() {
        this.setFocusPaused(true);
    }

    onBlur() {
        this.setFocusPaused(false);
    }

    dispose() {
        this.toggleSub?.dispose();
        clearTimeout(this.timeoutId);
    }

    private setHoverPaused(paused: boolean) {
        if (!this.duration) {
            return;
        }
        this.hoverPaused = paused;
        this.syncPauseState();
    }

    private setFocusPaused(paused: boolean) {
        if (!this.duration) {
            return;
        }
        this.focusPaused = paused;
        this.syncPauseState();
    }

    /** Pause or resume the countdown when hover/focus interaction changes. */
    private syncPauseState() {
        if (!this.isToggled()) {
            return;
        }
        const shouldPause = this.hoverPaused || this.focusPaused;
        if (shouldPause === this.isPaused()) {
            return;
        }
        if (shouldPause) {
            this.pauseTimer();
        } else {
            this.resumeTimer();
        }
    }

    private startTimer() {
        clearTimeout(this.timeoutId);
        this.timeoutId = undefined;
        if (!this.remainingMs) {
            return;
        }
        this.deadlineAt = Date.now() + this.remainingMs;
        this.timeoutId = window.setTimeout(() => this.isToggled(false), this.remainingMs);
    }

    private pauseTimer() {
        if (this.deadlineAt) {
            clearTimeout(this.timeoutId);
            this.timeoutId = undefined;
            this.remainingMs = Math.max(0, this.deadlineAt - Date.now());
            this.deadlineAt = undefined;
        }
        this.isPaused(true);
    }

    private resumeTimer() {
        this.isPaused(false);
        this.startTimer();
    }

}
